import { LeafMentionSuggestion, MentionSuggestion } from '../components/mentions/MentionsList';

export type SuggestionReference = { suggestion: LeafMentionSuggestion; parents: MentionSuggestion[] };
export type SuggestionSearchResult = { suggestion: LeafMentionSuggestion; parents: MentionSuggestion[]; match: string };
type MentionsProviderOptions = { temporal?: boolean };

const traverse = (
  suggestions: MentionSuggestion[],
  parents: MentionSuggestion[],
  callback?: (mention: LeafMentionSuggestion, parents: MentionSuggestion[]) => void,
  filter?: (mention: MentionSuggestion) => boolean,
): MentionSuggestion[] => {
  const filteredSuggestions: MentionSuggestion[] = [];

  for (const suggestion of suggestions) {
    if (filter && !filter(suggestion)) continue;

    if (suggestion.suggestions) {
      const filteredChildren = traverse(suggestion.suggestions, [...parents, suggestion], callback, filter);

      // Keep suggestions with empty arrays if they're not loaded yet
      if (filteredChildren.length > 0 || suggestion.loaded === false) {
        filteredSuggestions.push({ ...suggestion, suggestions: filteredChildren });
      }
    } else {
      if (filter && filter(suggestion)) filteredSuggestions.push(suggestion);
      if (callback && suggestion.slug) callback(suggestion, parents);
    }
  }

  return filteredSuggestions;
};

export default class MentionsProvider {
  private suggestionsMap: Map<string, SuggestionReference> = new Map();
  // Leaves seen in server search results. Kept for the provider's lifetime so a chip picked
  // from a search hit still resolves after the search is cleared and `suggestionsMap` is rebuilt.
  private searchIndex: Map<string, SuggestionReference> = new Map();
  private updateCallbacks: Set<() => void> = new Set();

  suggestions: MentionSuggestion[];
  availableSuggestions: MentionSuggestion[];
  remoteSearch: { query: string; suggestions: MentionSuggestion[] } | null = null;
  temporal: boolean;

  constructor(suggestions: MentionSuggestion[], options: MentionsProviderOptions = {}) {
    this.suggestions = this.normalizeSuggestions(suggestions);
    this.temporal = options.temporal === true;
    this.availableSuggestions = traverse(
      this.suggestions,
      [],
      undefined,
      suggestion => 'available' in suggestion && suggestion.available,
    );

    this.prepare();
  }

  private normalizeSuggestions(suggestions: MentionSuggestion[]): MentionSuggestion[] {
    return suggestions.map(suggestion => ({
      ...suggestion,
      loaded: suggestion.loaded ?? true, // Default to true if not specified
    }));
  }

  onUpdate(callback: () => void) {
    this.updateCallbacks.add(callback);
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  private notifyUpdate() {
    this.updateCallbacks.forEach(callback => callback());
  }

  setTemporal(nextTemporal: boolean) {
    if (this.temporal === nextTemporal) return;
    this.temporal = nextTemporal;
    this.notifyUpdate();
  }

  replaceSuggestions(nextSuggestions: MentionSuggestion[]) {
    this.suggestions = this.normalizeSuggestions(nextSuggestions);
    this.availableSuggestions = traverse(
      this.suggestions,
      [],
      undefined,
      suggestion => 'available' in suggestion && suggestion.available,
    );
    this.prepare();
    this.notifyUpdate();
  }

  getMentionBySlug(slug: string): SuggestionReference | { suggestion?: undefined; parents?: undefined } {
    return this.suggestionsMap.get(slug) ?? this.searchIndex.get(slug) ?? {};
  }

  replaceSearchSuggestions(query: string, nextSuggestions: MentionSuggestion[]) {
    this.remoteSearch = { query, suggestions: this.normalizeSuggestions(nextSuggestions) };
    traverse(this.remoteSearch.suggestions, [], (suggestion, parents) =>
      this.searchIndex.set(suggestion.slug, { suggestion, parents }),
    );
    this.notifyUpdate();
  }

  clearSearch() {
    if (!this.remoteSearch) return;
    this.remoteSearch = null;
    this.notifyUpdate();
  }

  flattenLeaves(suggestions: MentionSuggestion[]): SuggestionReference[] {
    const leaves: SuggestionReference[] = [];
    traverse(suggestions, [], (suggestion, parents) => leaves.push({ suggestion, parents }));
    return leaves;
  }

  searchMentions(
    query: string,
    { availableOnly = false }: { availableOnly?: boolean } = { availableOnly: false },
  ): SuggestionSearchResult[] {
    const suggestions = availableOnly ? this.availableSuggestions : this.suggestions;
    const results: SuggestionSearchResult[] = [];

    // Escape special regex characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    traverse(suggestions, [], (suggestion, parents) => {
      const label = suggestion.label || suggestion.slug || '';
      const match = label.match(new RegExp(escapedQuery, 'i'));

      if (match) {
        results.push({ suggestion, parents, match: match[0] });
      }
    });

    return results;
  }

  updateSuggestions(newSuggestions: MentionSuggestion[], key?: string) {
    if (key && key !== 'root') {
      // Add suggestions to the specific category/key that was opened
      this.addSuggestionsToKey(newSuggestions, key);
    } else {
      // Add to root level
      this.suggestions = [...this.suggestions, ...this.normalizeSuggestions(newSuggestions)];
    }

    // Rebuild available suggestions and map
    this.availableSuggestions = traverse(
      this.suggestions,
      [],
      undefined,
      suggestion => 'available' in suggestion && suggestion.available,
    );
    this.prepare();

    // Notify components to re-render
    this.notifyUpdate();
  }

  appendSuggestions(
    newSuggestions: MentionSuggestion[],
    key: string,
    { hasMore = false, nextOffset }: { hasMore?: boolean | undefined; nextOffset?: number | undefined } = {},
  ) {
    const appendToKey = (suggestions: MentionSuggestion[]): MentionSuggestion[] => {
      return suggestions.map(suggestion => {
        if (suggestion.slug === key || suggestion.label === key) {
          const existing = suggestion.suggestions ?? [];
          const seen = new Set(existing.map(item => item.slug).filter(Boolean));

          return {
            ...suggestion,
            suggestions: [
              ...existing,
              ...this.normalizeSuggestions(newSuggestions).filter(item => !seen.has(item.slug)),
            ],
            loaded: true,
            has_more: hasMore,
            next_offset: nextOffset,
          } as MentionSuggestion;
        } else if (suggestion.suggestions) {
          return { ...suggestion, suggestions: appendToKey(suggestion.suggestions) } as MentionSuggestion;
        }
        return suggestion;
      });
    };

    this.suggestions = appendToKey(this.suggestions);
    this.availableSuggestions = traverse(
      this.suggestions,
      [],
      undefined,
      suggestion => 'available' in suggestion && suggestion.available,
    );
    this.prepare();
    this.notifyUpdate();
  }

  setLoadingState(key: string, isLoading: boolean) {
    // Simply update the loaded state - false means loading, true means loaded
    const updateLoadingStateRecursive = (suggestions: MentionSuggestion[]): MentionSuggestion[] => {
      return suggestions.map(suggestion => {
        if (suggestion.slug === key || suggestion.label === key) {
          return { ...suggestion, loaded: !isLoading };
        } else if (suggestion.suggestions) {
          return {
            ...suggestion,
            suggestions: updateLoadingStateRecursive(suggestion.suggestions),
          };
        }
        return suggestion;
      });
    };

    this.suggestions = updateLoadingStateRecursive(this.suggestions);
    this.availableSuggestions = traverse(
      this.suggestions,
      [],
      undefined,
      suggestion => 'available' in suggestion && suggestion.available,
    );
    this.prepare();
    this.notifyUpdate();
  }

  private addSuggestionsToKey(newSuggestions: MentionSuggestion[], key: string) {
    const updateSuggestionsRecursive = (suggestions: MentionSuggestion[]): MentionSuggestion[] => {
      return suggestions.map(suggestion => {
        if (suggestion.slug === key || suggestion.label === key) {
          // Found the target category, replace its suggestions with new ones
          return {
            ...suggestion,
            suggestions: this.normalizeSuggestions(newSuggestions),
            loaded: true, // Mark as loaded
          } as MentionSuggestion;
        } else if (suggestion.suggestions) {
          // Recursively search in children
          return {
            ...suggestion,
            suggestions: updateSuggestionsRecursive(suggestion.suggestions),
          } as MentionSuggestion;
        }
        return suggestion;
      });
    };

    this.suggestions = updateSuggestionsRecursive(this.suggestions);
  }

  private prepare() {
    this.suggestionsMap.clear();
    traverse(this.suggestions, [], (suggestion, parents) =>
      this.suggestionsMap.set(suggestion.slug, { suggestion, parents }),
    );
  }
}
